/**
 * SDSU brand colors.
 *
 * Source: SDSU Brand Guidelines, Colors page
 * (https://brand.sdsu.edu/identity/design-elements/colors, fetched 2026-08-30,
 * W44 S0 fact sheet #14): "Our leading colors are red and black."
 *
 *   Bright Red  #D41736 (primary)          Black    #000000 (co-leading)
 *   Dark Red    #A6192E (primary, dark)     Charcoal #2D2828
 *   Secondary: Bright Teal #00A39D, Dark Teal #008080, Light Gray #CDCDC8
 *
 * Contrast (WCAG, measured): Bright Red on white 5.28:1 (passes AA for white
 * text, so it stays the primary DEFAULT); Dark Red 7.50:1 is the `-strong`
 * tier for solid fills.
 *
 * Accent: the S1 decision was black with "teal only if contrast checks fail";
 * they failed — the theme system ships no dark-mode variant for the accent,
 * and `text-accent` is consumed by real surfaces (override pill, override
 * progress fill, celebration icon), so pure black rendered black-on-charcoal
 * in dark appearance (PR #281 review). Dark Teal #008080 is the brand's own
 * secondary: 4.77:1 on white (AA), 3.85:1 on the warm charcoal #161412
 * (AA-large). No single hue clears 4.5:1 on BOTH surfaces (the luminance
 * windows do not overlap — UMD gold fails on white today), so this is the
 * best two-surface choice; a per-theme accent-dm variant is the durable fix.
 *
 * Brand mark: two red brands collide in `BrandMark.test.tsx`'s cross-theme
 * distance check (RGB distance >= 50 between every pair of mark faces).
 * Bright Red vs UMD Red was 37.9 (light) / 22.4 (dark). Decision: the LIGHT
 * front face is Dark Red 166 25 46 (68.6 from UMD's 224 58 62) and the DARK
 * front face is Bright Red 212 23 54 (120 from UMD's dm 244 118 120) — SDSU
 * reads deeper than UMD in light mode and truer in dark mode. The ramps are
 * derived from those two anchors.
 *
 * The 50-900 ladders are HSL interpolations around the brand values (SDSU
 * publishes no extended palette) — brand-aligned, not officially endorsed.
 * Logo policy mirrors UMD/SJSU: text-only branding, no Aztec/wordmark asset
 * embedded.
 */
import type { ThemeColors } from '../types';

export const sdsuColors: ThemeColors = {
  // Primary — SDSU Bright Red (#D41736 = rgb(212, 23, 54))
  '--color-primary':     '212 23 54',
  // -strong: SDSU Dark Red (#A6192E, 7.50:1 with white) for white-text fills.
  '--color-primary-strong': '166 25 46',
  '--color-primary-50':  '254 242 244',
  '--color-primary-100': '253 226 231',
  '--color-primary-200': '251 197 206',
  '--color-primary-300': '246 152 168',
  '--color-primary-400': '236 95 120',
  '--color-primary-500': '212 23 54',
  '--color-primary-600': '189 20 48',
  '--color-primary-700': '166 25 46',
  '--color-primary-800': '134 20 38',
  '--color-primary-900': '104 16 30',

  // Accent — SDSU Dark Teal (#008080 = rgb(0, 128, 128)); see header.
  '--color-accent':     '0 128 128',
  '--color-accent-50':  '236 249 248',
  '--color-accent-100': '209 240 239',
  '--color-accent-200': '167 224 222',
  '--color-accent-300': '112 200 197',
  '--color-accent-400': '52 168 165',
  '--color-accent-500': '0 128 128',
  '--color-accent-600': '0 110 110',
  '--color-accent-700': '0 92 92',
  '--color-accent-800': '0 72 72',
  '--color-accent-900': '0 52 52',

  // Brand-mark faces (components/ui/BrandMark.tsx). Light: Dark Red front
  // (identity-safe vs UMD, see header), two ~12% darker steps for the
  // oblique faces. Dark: Bright Red front, stepping down through Dark Red.
  '--color-mark-front': '166 25 46',
  '--color-mark-top':   '128 18 35',
  '--color-mark-side':  '92 12 25',
  '--color-mark-front-dm': '212 23 54',
  '--color-mark-top-dm':   '166 25 46',
  '--color-mark-side-dm':  '120 18 34',
};
